// src/components/mfa/MfaSetupModal.jsx
//
// Turning on two-step verification, in three steps:
//   1. password   POST /auth/mfa/enroll  -> a pending setup key
//   2. key        scan the QR code (qrcode.react, approved 2026-10-30) or type
//                 the key into an authenticator app, POST /auth/mfa/confirm
//   3. codes      the recovery codes, shown once
// Leaving at step 2 keeps the setup pending (Settings offers to finish or
// cancel it); nothing changes at sign-in until step 2 succeeds. The password is
// cleared from state as soon as the server has it.
import React, { useState } from 'react';
import { Check, Copy, ExternalLink, Lock, ShieldCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { apiCall } from '../../utils/api';
import { isCompleteCode, mfaErrorMessage, sanitizeTotpInput, setupKeyGroups } from '../../lib/mfa';
import MfaModalShell, { ErrorBanner } from './MfaModalShell';
import { AcknowledgeAndClose, RecoveryCodesPanel } from './RecoveryCodesPanel';

export function PasswordStep({ password, onPasswordChange, error, onSubmit }) {
  return (
    <>
      <p className="text-sm text-[#a0a0a0]">
        Confirm it is you before we create your setup key.
      </p>
      <ErrorBanner message={error} />
      <div>
        <Label htmlFor="mfa-setup-password">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
          <Input
            id="mfa-setup-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && password) onSubmit?.(); }}
            className="pl-10"
          />
        </div>
      </div>
    </>
  );
}

// The typed-key fallback for when scanning is not possible. Its own component so
// the expanded state renders in tests without a click.
export function ManualKeyDetails({ enrollment, copied, onCopyKey }) {
  return (
    <div className="mt-3 p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg space-y-3">
      <p className="text-xs text-[#a0a0a0]">
        In your app, choose to add an account with a setup key. The key is time based.
      </p>
      <div>
        <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">Account</div>
        <div className="text-sm text-white">{enrollment.issuer} ({enrollment.account})</div>
      </div>
      <div>
        <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">Setup key</div>
        <div className="flex items-center gap-2">
          <div className="flex-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-sm text-white" aria-label="Setup key">
            {setupKeyGroups(enrollment.secret_display).map((group, i) => (
              <span key={`${i}-${group}`}>{group}</span>
            ))}
          </div>
          <button
            type="button"
            onClick={onCopyKey}
            className="h-10 w-10 flex items-center justify-center bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:text-white hover:bg-[#222] transition-colors flex-shrink-0"
            aria-label="Copy setup key"
          >
            {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SetupKeyStep({ enrollment, code, onCodeChange, error, copied, onCopyKey, onSubmit }) {
  const [showKey, setShowKey] = useState(false);
  return (
    <>
      <ol className="space-y-4 text-sm text-[#a0a0a0] list-decimal list-inside">
        <li>
          Open an authenticator app such as 1Password, Google Authenticator,
          Microsoft Authenticator or Authy, and scan this code.
          {/* Drawn here from the otpauth URI the page already holds; nothing is
              sent to an outside QR service. White backing so every scanner reads it. */}
          <div className="mt-3 flex justify-center">
            <div className="p-3 bg-white rounded-lg" data-testid="mfa-qr">
              <QRCodeSVG
                value={enrollment.otpauth_uri}
                size={168}
                level="M"
                marginSize={1}
                bgColor="#ffffff"
                fgColor="#000000"
                title="QR code to add Connex to your authenticator app"
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              aria-expanded={showKey}
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
            >
              {showKey ? 'Hide the setup key' : 'Cannot scan? Enter a setup key instead'}
            </button>
            <a
              href={enrollment.otpauth_uri}
              className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              On your phone? Open in your app
            </a>
          </div>
          {showKey && <ManualKeyDetails enrollment={enrollment} copied={copied} onCopyKey={onCopyKey} />}
        </li>
        <li>Enter the 6-digit code the app shows for Connex.</li>
      </ol>
      <ErrorBanner message={error} />
      <div>
        <Label htmlFor="mfa-setup-code">Verification code</Label>
        <Input
          id="mfa-setup-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          value={code}
          onChange={(e) => onCodeChange(sanitizeTotpInput(e.target.value))}
          onKeyDown={(e) => { if (e.key === 'Enter' && isCompleteCode(code, 'totp')) onSubmit?.(); }}
          className="font-mono tracking-[0.4em]"
        />
      </div>
    </>
  );
}

const STEP_LABELS = { password: 'Step 1 of 3', key: 'Step 2 of 3', codes: 'Step 3 of 3' };

export default function MfaSetupModal({ account, onCancel, onDone }) {
  const [step, setStep] = useState('password');
  const [password, setPassword] = useState('');
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const call = async (path, body) => {
    setBusy(true);
    setError('');
    try {
      const res = await apiCall(path, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (res.ok) return data;
      setError(mfaErrorMessage(res.status, data.detail));
    } catch (err) {
      console.error('Two-step setup request failed', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
    return null;
  };

  const handleEnroll = async () => {
    const data = await call('/auth/mfa/enroll', { password });
    if (data) {
      setPassword('');
      setEnrollment(data);
      setStep('key');
    }
  };

  const handleConfirm = async () => {
    const data = await call('/auth/mfa/confirm', { code });
    if (data) {
      setEnrollment(null);
      setCodes(data.recovery_codes || []);
      setStep('codes');
    } else {
      setCode('');
    }
  };

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(enrollment.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard write failed', err);
    }
  };

  const footers = {
    password: (
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={handleEnroll} disabled={busy || !password}>
          {busy ? 'Checking...' : 'Continue'}
        </Button>
      </div>
    ),
    key: (
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Finish later</Button>
        <Button onClick={handleConfirm} disabled={busy || !isCompleteCode(code, 'totp')}>
          {busy ? 'Verifying...' : 'Turn on'}
        </Button>
      </div>
    ),
    codes: (
      <AcknowledgeAndClose acknowledged={acknowledged} onAcknowledge={setAcknowledged} onClose={onDone} />
    ),
  };

  return (
    <MfaModalShell
      icon={ShieldCheck}
      tone={step === 'codes' ? 'green' : 'blue'}
      step={STEP_LABELS[step]}
      title={step === 'codes' ? 'Two-step verification is on' : 'Set up two-step verification'}
      subtitle={step === 'codes'
        ? 'From now on, signing in asks for a code from your authenticator app.'
        : 'A code from your phone, on top of your password, every time you sign in.'}
      footer={footers[step]}
    >
      {step === 'password' && (
        <PasswordStep password={password} onPasswordChange={setPassword} error={error} onSubmit={busy ? undefined : handleEnroll} />
      )}
      {step === 'key' && enrollment && (
        <SetupKeyStep
          enrollment={enrollment}
          code={code}
          onCodeChange={setCode}
          error={error}
          copied={copied}
          onCopyKey={handleCopyKey}
          onSubmit={busy ? undefined : handleConfirm}
        />
      )}
      {step === 'codes' && <RecoveryCodesPanel codes={codes} account={account} />}
    </MfaModalShell>
  );
}
